import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';

import OutputText from '@ey/canvascoreservices/OutputText';

import CountDisplay, {CD_BADGE} from '../../../Common/CountDisplay';

import {labels} from '../../../../util/utils';
import {rootLevelId, engagementStatuses, gaLinkStatusId} from '../../../../util/uiconstants';

import ContactTable from './ContactTable';
import StyledEmptyMessage from '../StyledEmptyMessage';
import CountSummary from './CountSummary';
import AddContact from '../Toolbar/AddContact';

import StyledEntityAccordion from './StyledEntityAccordion';
import getUserPermissions from '../getUserPermissions';
import StyledTrigger from './StyledTrigger';
import EditEntity from './EditEntity';
import StyledSummary from './StyledSummary';
import StyledEntityAccordionContent from './StyledEntityAccordionContent';

const EntityAccordion = ({entity, userSearch = '', project, isProjectOwner}) => {
	const [isOpen, setIsOpen] = useState(false);
	const {entityName, engagementCount, contactsCount} = entity;

	const entityNode = project?.orgStructure?.find((node) => node.entityId === entity.entityId);
	const engagementStatus = entityNode?.engagementStatus;
	const linkStatusId = entityNode?.gaLinkStatusId;

	const hasContacts = contactsCount > 0;

	const isRootLevel = entity.level === rootLevelId;
	const isGRAProject = project.isGroupRiskAssessmentProject;

	const isGRARootLevel = isRootLevel && isGRAProject;

	const showEditMenu = isGRAProject && isProjectOwner && !isRootLevel;

	const {addContactPermission} = useSelector((state) =>
		getUserPermissions({
			userPreferences: state.userPreferences,
			entities: (entityNode && [entityNode]) || [],
			userList: state.project?.users
		})
	);

	const renderName = () => {
		let content = entityName;
		if (isGRAProject && isRootLevel) content = `${entityName} - ${labels.projectOwners}`;

		return (
			<OutputText ellipsis title={content} className="name">
				{content}
			</OutputText>
		);
	};

	const renderEngagements = () => {
		if (isGRARootLevel) return false;

		const props = {
			count: engagementCount,
			label: labels.engagementsLabel,
			singleLabel: labels.engagementLabel,
			variant: CD_BADGE,
			className: 'engagements'
		};

		return <CountDisplay {...props} />;
	};

	const renderContacts = () => {
		const props = {
			count: contactsCount,
			label: labels.contacts,
			singleLabel: labels.singularContact,
			variant: CD_BADGE,
			className: 'contacts'
		};
		return <CountDisplay {...props} />;
	};

	const isDisabled = () => {
		return engagementStatus === engagementStatuses.Archived || linkStatusId === gaLinkStatusId.unlinked;
	};

	const renderEditEntityMenu = () => {
		const props = {
			entity,
			project,
			isDisabled: isDisabled(),
			className: 'menu'
		};

		return <EditEntity {...props} />;
	};

	const renderTrigger = () => {
		const props = {};
		if (isGRAProject && isProjectOwner) props.className = 'with-menu';

		return (
			<StyledTrigger {...props}>
				{renderName()}
				{renderEngagements()}
				{renderContacts()}
				{showEditMenu && renderEditEntityMenu()}
			</StyledTrigger>
		);
	};

	const renderEmpty = () => {
		const props = {size: 'L', weight: 'light'};

		//TODO: fix EmptyMessage component to use that.
		return (
			<StyledEmptyMessage>
				<OutputText {...props}>{labels.noContacts}</OutputText>
			</StyledEmptyMessage>
		);
	};

	const renderSummary = () => {
		return (
			<StyledSummary>
				{addContactPermission && <AddContact />}
				<CountSummary entity={entity} isGRARootLevel={isGRARootLevel} />
			</StyledSummary>
		);
	};

	const handleOnOpenAccordion = () => {
		setIsOpen(!isOpen);
	};

	const accordionProps = {
		trigger: renderTrigger,
		onClose: handleOnOpenAccordion,
		onOpen: handleOnOpenAccordion
	};

	return (
		<StyledEntityAccordion {...accordionProps}>
			<StyledEntityAccordionContent>
				{renderSummary()}
				{hasContacts && isOpen && <ContactTable {...entity} />}
				{!hasContacts && renderEmpty()}
			</StyledEntityAccordionContent>
		</StyledEntityAccordion>
	);
};

const contactShape = {};

const entityShape = {
	id: PropTypes.string,
	entityName: PropTypes.string,
	entityId: PropTypes.string,
	engagements: PropTypes.arrayOf(PropTypes.number),
	level: PropTypes.number,
	contacts: PropTypes.arrayOf(PropTypes.shape(contactShape)),

	engagementCount: PropTypes.number,
	contactsCount: PropTypes.number,
	dashboardCount: PropTypes.number,
	contactListCount: PropTypes.number
};

EntityAccordion.propTypes = {
	entity: PropTypes.shape(entityShape).isRequired,
	userSearch: PropTypes.string
};

export default EntityAccordion;
